import { useEffect, useRef, useState as useStateRaw, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Seo from '@/components/seo/Seo';
import HeroSection from './components/HeroSection';
import SchoolDefinition from './components/SchoolDefinition';
import HistoryTimeline from './components/HistoryTimeline';
import GlobalDirection from './components/GlobalDirection';
import JapanPosition from './components/JapanPosition';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import RelatedPages from './components/RelatedPages';
import { butlerSchoolFaqKeys } from './faqKeys';

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useStateRaw(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

function renderParagraphs(text: string, className: string) {
  return text.split(/\n{2,}/).map((paragraph, index) => (
    <p key={index} className={className}>
      {paragraph}
    </p>
  ));
}

export default function ButlerSchoolPage() {
  const { lang } = useLanguageContext();
  const { t } = useTranslation();

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: lang === 'en' ? 'Home' : 'ホーム',
        item: 'https://butler-academy.jp/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: lang === 'en' ? 'What Is a Butler School?' : '執事学校とは？',
        item: 'https://butler-academy.jp/butler-school',
      },
    ],
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: butlerSchoolFaqKeys.map((faq) => ({
      '@type': 'Question',
      name: String(t(faq.q)).replace(/\n/g, ''),
      acceptedAnswer: {
        '@type': 'Answer',
        text: String(t(faq.a)).replace(/\n/g, ''),
      },
    })),
  };

  return (
    <>
      <Header />
      <main>
        <Seo route="/butler-school" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
        />

        <HeroSection />

        <FadeInSection>
          <SchoolDefinition />
        </FadeInSection>

        <FadeInSection>
          <HistoryTimeline />
        </FadeInSection>

        <FadeInSection>
          <GlobalDirection />
        </FadeInSection>

        <FadeInSection>
          <JapanPosition />
        </FadeInSection>

        <FadeInSection>
          <FAQSection />
        </FadeInSection>

        <FadeInSection>
          <CTASection />
        </FadeInSection>

        <FadeInSection>
          <RelatedPages />
        </FadeInSection>

        <div className="bg-background-50 py-8">
          {renderParagraphs(
            String(t('butlerSchool.lastUpdated')),
            'text-center text-[13px] text-foreground-400 tracking-[0.05em]'
          )}
        </div>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
          aria-label={t('common.backToTop')}
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </main>
      <Footer />
    </>
  );
}
